import React, { useRef } from 'react';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import type { TemplateSelectorItem } from '../../organisms/TreeMap/types';
import './TemplateSelector.scss';

export type { TemplateSelectorItem } from '../../organisms/TreeMap/types';

export interface TemplateSelectorProps {
  /** Available templates to select from */
  templates: TemplateSelectorItem[];
  /** Currently selected template */
  selectedTemplate: TemplateSelectorItem | null;
  /** Callback when template selection changes (undefined when locked) */
  onTemplateChange?: (template: TemplateSelectorItem) => void;
  /** Whether the selector is locked (shows template name only, no dropdown) */
  locked?: boolean;
  /** Whether templates are loading */
  loading?: boolean;
  /** Placeholder text when no template is selected */
  placeholder?: string;
  /** Additional CSS class name */
  className?: string;
}

/**
 * Template selector component for TreeMap.
 * Shows current template name with a dropdown menu to switch templates.
 * Similar to Atlas config selector pattern.
 */
const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  templates,
  selectedTemplate,
  onTemplateChange,
  locked = false,
  loading = false,
  placeholder = 'Select Template',
  className = '',
}) => {
  const menuRef = useRef<Menu>(null);

  // Build menu items from templates
  const menuItems: MenuItem[] = templates.map((template) => ({
    label: template.name,
    icon: template.isSystem ? 'pi pi-building' : 'pi pi-user',
    className: template.system_name === selectedTemplate?.system_name ? 'template-selector__menu-item--active' : '',
    command: () => {
      if (onTemplateChange && template.system_name !== selectedTemplate?.system_name) {
        onTemplateChange(template);
      }
    },
  }));

  const handleButtonClick = (e: React.MouseEvent) => {
    if (!locked && menuRef.current) {
      menuRef.current.toggle(e);
    }
  };

  const templateName = selectedTemplate?.name || placeholder;
  const buttonIcon = loading ? 'pi pi-spinner pi-spin' : 'pi pi-chevron-down';

  return (
    <div className={`template-selector ${className} ${locked ? 'template-selector--locked' : ''}`}>
      <ButtonAtom
        variant="secondary"
        shape="pill"
        size="small"
        icon={locked ? undefined : buttonIcon}
        iconPos="right"
        label={templateName}
        onClick={handleButtonClick}
        disabled={locked}
        className="template-selector__button"
      />

      {!locked && (
        <Menu
          model={menuItems}
          popup
          ref={menuRef}
          className="template-selector__menu"
        />
      )}
    </div>
  );
};

export default TemplateSelector;
